import { getApiBase } from '../config/apiBase';

class AuthService {
  constructor() {
    this.oktaAuth = null;
  }

  setOktaAuth(oktaAuth) {
    this.oktaAuth = oktaAuth;
  }

  async getAccessToken() {
    if (!this.oktaAuth) {
      throw new Error('Okta Auth not initialized');
    }

    try {
      const tokenManager = this.oktaAuth.tokenManager;
      let accessToken = await tokenManager.get('accessToken');
      
      if (!accessToken) {
        // Try to get tokens from authState first
        const authState = await this.oktaAuth.authStateManager.getAuthState();
        if (authState && authState.accessToken) {
          return authState.accessToken.accessToken;
        }
        throw new Error('No access token available');
      }

      // Check if token is expired or about to expire
      if (this.isTokenExpired(accessToken)) {
        try {
          // First try to renew using refresh token if available
          const refreshToken = await tokenManager.get('refreshToken');
          if (refreshToken && !this.isTokenExpired(refreshToken)) {
            await this.oktaAuth.token.renewTokens();
            accessToken = await tokenManager.get('accessToken');
          } else {
            // Fallback to standard renewal
            await this.oktaAuth.tokenManager.renew('accessToken');
            accessToken = await tokenManager.get('accessToken');
          }
        } catch (renewError) {
          console.warn('Token renewal failed, token might still be valid:', renewError);
          // If renewal fails but token isn't completely expired, try to use it
          if (!this.isTokenExpired(accessToken, 0)) {
            return accessToken.accessToken;
          }
          throw renewError;
        }
      }

      return accessToken ? accessToken.accessToken : null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  }

  isTokenExpired(token, bufferMinutes = 5) {
    if (!token || !token.expiresAt) {
      return true;
    }
    
    // Check if token expires within the buffer time (default 5 minutes)
    const bufferTime = Date.now() + (bufferMinutes * 60 * 1000);
    return token.expiresAt * 1000 < bufferTime;
  }

  // Read the Okta refresh token (string + expiry) from the token manager, if
  // one exists. offline_access is configured (oktaConfig.js) so the SPA holds a
  // rotating refresh token; we forward it to the server at establish time so
  // Lyric can renew a deployed app's forwarded access token server-side while
  // the app sits idle (Option 1). Returns null if unavailable — forwarding is
  // best-effort and must never block login.
  async getRefreshToken() {
    if (!this.oktaAuth) return null;
    try {
      const rt = await this.oktaAuth.tokenManager.get('refreshToken');
      if (rt && rt.refreshToken) {
        return { refreshToken: rt.refreshToken, expiresAt: rt.expiresAt || null };
      }
    } catch (err) {
      console.warn('getRefreshToken failed (continuing):', err.message);
    }
    return null;
  }

  async handleAuthenticationError() {
    if (!this.oktaAuth) {
      return;
    }

    try {
      // First try to check if we have valid tokens
      const tokenManager = this.oktaAuth.tokenManager;
      const refreshToken = await tokenManager.get('refreshToken');
      
      if (refreshToken && !this.isTokenExpired(refreshToken)) {
        // Try to renew using refresh token
        await this.oktaAuth.token.renewTokens();
      } else {
        // Try standard token renewal
        await this.oktaAuth.tokenManager.renew('accessToken');
        await this.oktaAuth.tokenManager.renew('idToken');
      }
    } catch (error) {
      console.error('Token renewal failed:', error);
      
      // Clear any invalid tokens before redirecting
      try {
        await this.oktaAuth.tokenManager.clear();
      } catch (clearError) {
        console.warn('Failed to clear tokens:', clearError);
      }
      
      // Redirect to login
      this.oktaAuth.signInWithRedirect();
    }
  }

  async verifyAuthentication(retryCount = 0) {
    const maxRetries = 2;
    
    try {
      const token = await this.getAccessToken();
      console.log('🔍 Verifying authentication...');
      console.log('Token length:', token ? token.length : 'null');
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'null');
      
      // Resolve the API base via the shared helper (runtime config → build env
      // → same-origin → localhost). NEVER hardcode localhost on a real host.
      const apiUrl = getApiBase();
      
      console.log('Making request to:', `${apiUrl}/api/verify`);
      
      const response = await fetch(`${apiUrl}/api/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });

      if (!response.ok) {
        if (response.status === 401 && retryCount < maxRetries) {
          console.log('Token verification failed, attempting to renew token...');
          try {
            await this.oktaAuth.tokenManager.renew('accessToken');
            return this.verifyAuthentication(retryCount + 1);
          } catch (renewError) {
            console.error('Token renewal failed:', renewError);
            throw new Error('Authentication expired');
          }
        }
        throw new Error(`Token verification failed with status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('Network error during authentication verification:', error);
        throw new Error('Network connection issue - please check if the backend server is running');
      }

      console.error('Authentication verification failed:', error);
      throw error;
    }
  }

  // ── Server-side session lifecycle ──────────────────────────────────────
  // After Okta PKCE login the React app holds a Bearer JWT in memory, which
  // is fine for our JSON API surface. But /apps/<name>/* is a browser-level
  // proxy that can't read Bearer headers — it needs a cookie-based session.
  // establishSession() trades the JWT for an HttpOnly session cookie that
  // gates the deployed apps. Safe to call repeatedly.
  apiBaseUrl() {
    return getApiBase();
  }

  async establishSession() {
    const token = await this.getAccessToken();
    if (!token) throw new Error('No access token to establish session');
    // Best-effort: forward the refresh token so the server can renew the
    // deployed-app access token while the app is idle (Option 1). Never block
    // session establishment if it's missing. The clientId/issuer are sent too:
    // a refresh-token grant must use the SAME public SPA client_id + issuer that
    // minted the token, which the server can't reliably guess from its own env.
    const rt = await this.getRefreshToken();
    const opts = (this.oktaAuth && this.oktaAuth.options) || {};
    const body = rt
      ? JSON.stringify({
          refreshToken: rt.refreshToken,
          refreshTokenExp: rt.expiresAt,
          clientId: opts.clientId || null,
          issuer: opts.issuer || null,
        })
      : undefined;
    const res = await fetch(`${this.apiBaseUrl()}/api/session/establish`, {
      method: 'POST',
      credentials: 'include', // critical — sends/receives the session cookie
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `establishSession failed: ${res.status}`);
    }
    return res.json();
  }

  async destroySession() {
    try {
      await fetch(`${this.apiBaseUrl()}/api/session/destroy`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      // Logout should never block on a failed cookie clear — the Okta signOut
      // is the source of truth for "you're logged out".
      console.warn('destroySession failed (continuing):', err.message);
    }
  }
}

export const authService = new AuthService();
